/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useUserData,
  useAppDispatch,
  useSelectedCity,
  useUserReducer,
  useGetBusinessData,
} from "../../../Utility";
import { onsaveSelectedCity } from "../../../Store/Slices/utilitySlice";
import { cities } from "../../../config/appData";

import "./ProfilePage.css";
import { persistor } from "../../../Store/Store";
import {
  capsFirstLetter,
  clearLoginDataFunction,
  formatDate,
  isBusinessUser,
} from "../../../utils/UtilityFunctions";
import BusinessCompletionProfile from "./BusinessCompletionProfile";
import { onGetEnquiries } from "../../../Store/Slices/enquirySlice";
import {
  onGetUsers,
  onPostUsers,
  onPostUsersReset,
} from "../../../Store/Slices/userSlice";
import toast from "react-hot-toast";

const ENQUIRY_LIST_KEY = "Your Enquiries";
const ENQUIRIES_PAGE_SIZE = 5;
/** Map URL section param to sidebar id: personal, enquiries, 1, 2, 3, 4, ratings */
const sectionParamToId = (param, isBusiness) => {
  if (!param) return null;
  if (isBusiness && param === "ratings") return "ratings";
  if (isBusiness && [1, 2, 3, 4].includes(Number(param))) return Number(param);
  if (!isBusiness && param === "personal") return "personal";
  if (!isBusiness && param === "enquiries") return "Your Enquiries";
  return null;
};

/** Map sidebar id to URL param string */
const sectionIdToParam = (id) => {
  if (id === "Your Enquiries") return "enquiries";
  if (id === "ratings") return "ratings";
  return String(id);
};

function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.utilityReducer?.isLoggedIn);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isLogoutSuccess, setIsLogoutSuccess] = useState(false);
  const userData = useUserReducer();

  const [isClosing, setIsClosing] = useState(false);
  /** 'personal' | 1 | 2 | 3 | 4 | 'Your Enquiries' - which section is shown in main content */
  const isBusiness = isBusinessUser();
  const defaultSection = isBusiness ? 1 : "personal";
  const sectionFromUrl = sectionParamToId(
    searchParams.get("section"),
    isBusiness,
  );
  const [activeSection, setActiveSection] = useState(
    sectionFromUrl ?? defaultSection,
  );

  // Hide footer when logout modal is open
  useEffect(() => {
    if (showLogoutModal) {
      document.body.classList.add("logout-modal-open");
    } else {
      document.body.classList.remove("logout-modal-open");
    }
    return () => document.body.classList.remove("logout-modal-open");
  }, [showLogoutModal]);

  // Sync URL -> state (e.g. back/forward, direct link)
  useEffect(() => {
    const param = sectionParamToId(searchParams.get("section"), isBusiness);
    if (param != null) setActiveSection(param);
    else {
      setActiveSection(defaultSection);
      setSearchParams(
        { section: sectionIdToParam(defaultSection) },
        { replace: true },
      );
    }
  }, [searchParams, isBusiness, defaultSection]);

  const profileUserData = useUserData();

  const businessName = useGetBusinessData()?.getBusinessData?.business_name;
  const isBusinessLogin = isBusinessUser();
  const userName = isBusinessLogin
    ? businessName
    : profileUserData?.name || "User";
  const selectedCity = useSelectedCity();
  const reduxDispatch = useAppDispatch();
  const initialCity = profileUserData?.city || "";
  const [editName, setEditName] = useState(userName);
  const [editCity, setEditCity] = useState(initialCity);

  // Sync edit fields from profile only (do not use URL/params or selectedCity for city)
  useEffect(() => {
    const name = profileUserData?.name || "User";
    const city = userData?.getUsersData?.[0]?.cities;
    setEditName(name);
    setEditCity(city);
  }, [profileUserData?.name, profileUserData?.city, userData]);

  const hasDetailsChanged =
    editName?.trim() !== (userName || "").trim() ||
    editCity !== userData?.getUsersData?.[0]?.cities;

  const handleUpdateDetails = () => {
    if (!hasDetailsChanged) return;

    reduxDispatch(
      onPostUsers({
        name: editName?.trim(),
        cities: editCity,
        id: profileUserData?.id,
      }),
    );
    if (editCity !== selectedCity) {
      reduxDispatch(onsaveSelectedCity({ selectedCity: editCity }));
    }
  };

  const enquiryState = useSelector((state) => state.enquiryReducer);
  const enquiriesListRaw = enquiryState?.getData;
  const enquiriesList = Array.isArray(enquiriesListRaw) ? enquiriesListRaw : [];
  const [enquiriesVisibleCount, setEnquiriesVisibleCount] =
    useState(ENQUIRIES_PAGE_SIZE);

  const enquiriesLoading =
    enquiryState?.getLoading || userData?.getUsersLoading;
  const enquiriesError = enquiryState?.getError;
  const [enquiryDetailModal, setEnquiryDetailModal] = useState({
    open: false,
    enquiry: null,
  });
  const getInitials = (name) => {
    if (!name) return "U";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }

    const firstInitial = words[0].charAt(0).toUpperCase();
    const lastInitial = words[words.length - 1].charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  };

  const userInitials = getInitials(userName);
  const userEmail = profileUserData?.email || "";
  const userPhone = profileUserData?.phone || "";

  const personDetailsLoading =
    userData?.getUsersLoading || userData?.postUsersLoading;

  // Handle logout confirmation
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Confirm logout with smooth animation
  const handleConfirmLogout = () => {
    setIsLoggingOut(true);
    persistor.purge();
    // Show loading state briefly
    setTimeout(() => {
      setIsLoggingOut(false);
      setIsLogoutSuccess(true);

      // Show success animation, then logout
      setTimeout(() => {
        setIsClosing(true);
        setTimeout(() => {
          clearLoginDataFunction();
          setShowLogoutModal(false);
          setIsLogoutSuccess(false);
          setIsClosing(false);
          navigate("/");
        }, 600);
      }, 800);
    }, 500);
  };
  useEffect(() => {
    dispatch(onGetEnquiries());
  }, [dispatch]);
  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const openEnquiryDetail = (enquiry) => {
    setEnquiryDetailModal({ open: true, enquiry });
  };
  const closeEnquiryDetail = () => {
    setEnquiryDetailModal({ open: false, enquiry: null });
  };
  const handleAddAnotherEnquiry = () => {
    closeEnquiryDetail();
    navigate("/enquiry");
  };
  useEffect(() => {
    if (userData?.postUsersStatusCode === "200") {
      toast.success(userData?.postUsersMessage);
      dispatch(onPostUsersReset());
      dispatch(onGetUsers());
    } else if (userData?.postUsersStatusCode === "400") {
      toast.error(userData?.postUsersMessage);
      dispatch(onPostUsersReset());
    } else if (userData?.postUsersStatusCode) {
      toast.error(userData?.postUsersMessage);
      dispatch(onPostUsersReset());
    }
  }, [userData]);
  useEffect(() => {
    dispatch(onGetUsers());
  }, []);
  const formatEnquirySummary = (item) => {
    const venueName = item.reference_name;
    const location = item.city;
    if (venueName && location) return `Enquiry For ${venueName} (${location})`;
    if (venueName) return `Enquiry for ${venueName}`;
    if (location) return `Enquiry (${location})`;
    const eventType = item.eventType;

    const city = item.city || "";
    const parts = [eventType, city].filter(Boolean);
    return parts.length ? `Enquiry · ${parts.join(" · ")}` : "Enquiry";
  };

  /** Label map for enquiry detail – show all API keys with readable labels */
  const ENQUIRY_DETAIL_LABELS = {
    name: "Name",
    email: "Email",
    phone: "Phone",

    eventdate: "Event date",
    reference_name: "Enquiry For",
    eventtype: "Event type",

    nooguests: "Number of guests",
    city: "City / location",

    budget: "Budget",

    descrip: "Description ",

    type: "Type",
  };

  // Redirect to home if not logged in
  if (!isLoggedIn) {
    navigate("/");
    return null;
  }

  const sidebarSections = [
    ...(isBusiness
      ? [
          { id: 1, label: "Personal details", icon: "business" },
          { id: 2, label: "Venue details", icon: "venue" },
          { id: 3, label: "Description & amenities", icon: "amenities" },
          { id: 4, label: "Photos", icon: "photos" },
          { id: "ratings", label: "Business Ratings", icon: "ratings" },
        ]
      : [
          { id: "personal", label: "Personal details", icon: "profile" },
          { id: "Your Enquiries", label: "Your Enquiries", icon: "enquiry" },
        ]),
  ];

  return (
    <div className="profile-page">
      <div className="profile-container">
        {/* 1. Sidebar: Your profile heading + 4 options (Personal, Business, Venue, Amenities, Photos) + Logout */}
        <div className="profile-sidebar">
          <div className="profile-banner">
            {personDetailsLoading ? (
              <div className="profile-banner-skeleton" aria-hidden="true">
                <div className="profile-heading-skeleton skeleton-pulse" />
                <div className="profile-avatar-skeleton skeleton-pulse" />
                <div className="profile-name-skeleton skeleton-pulse" />
                <div className="profile-email-skeleton skeleton-pulse" />
              </div>
            ) : (
              <>
                <h2 className="profile-sidebar-heading">Your profile</h2>
                <div className="profile-avatar">{userInitials}</div>
                <p className="profile-name">{userName}</p>
                <p className="profile-email">{userEmail}</p>
              </>
            )}
          </div>

          <nav className="profile-nav">
            {sidebarSections.map((section) => (
              <button
                key={section.id}
                type="button"
                className={`profile-nav-item ${activeSection === section.id ? "active" : ""}`}
                onClick={() => {
                  setActiveSection(section.id);
                  setSearchParams({ section: sectionIdToParam(section.id) });
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  {section.icon === "profile" && (
                    <>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </>
                  )}
                  {section.icon === "business" && (
                    <>
                      <rect
                        x="2"
                        y="7"
                        width="20"
                        height="14"
                        rx="2"
                        ry="2"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                      <path
                        d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                    </>
                  )}
                  {section.icon === "venue" && (
                    <path
                      d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                  )}
                  {section.icon === "amenities" && (
                    <path
                      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                  )}
                  {section.icon === "photos" && (
                    <>
                      <rect
                        x="3"
                        y="3"
                        width="18"
                        height="18"
                        rx="2"
                        ry="2"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                      <circle
                        cx="8.5"
                        cy="8.5"
                        r="1.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                      <polyline
                        points="21 15 16 10 5 21"
                        stroke="currentColor"
                        strokeWidth="2"
                        fill="none"
                      />
                    </>
                  )}
                  {section.icon === "ratings" && (
                    <>
                      <polygon
                        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </>
                  )}
                  {section.icon === "enquiry" && (
                    <path
                      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                    />
                  )}
                </svg>
                <span>{section.label}</span>
              </button>
            ))}

            <div className="profile-nav-divider" />
            <button
              type="button"
              className="profile-nav-item"
              onClick={() => navigate("/forgot-password")}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Change password</span>
            </button>
            <button
              type="button"
              className="profile-nav-item profile-nav-logout"
              onClick={handleLogoutClick}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span>Logout</span>
            </button>
          </nav>
        </div>

        {/* 2. Main content: one area – shows Personal details OR selected business step (editable) */}
        <div className="profile-main">
          {activeSection === "personal" && (
            <div className="profile-content-single">
              {personDetailsLoading ? (
                <div
                  className="profile-personal-skeleton"
                  aria-hidden="true"
                  aria-busy="true"
                >
                  <div className="profile-header">
                    <div className="profile-skeleton-title skeleton-pulse" />
                    <div className="profile-skeleton-subtitle skeleton-pulse" />
                  </div>
                  <div className="profile-section">
                    <div className="profile-info-grid">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="profile-info-item profile-skeleton-info-item"
                        >
                          <div className="profile-skeleton-label skeleton-pulse" />
                          <div className="profile-skeleton-value skeleton-pulse" />
                        </div>
                      ))}
                    </div>
                    <div className="profile-update-actions">
                      <div className="profile-skeleton-btn skeleton-pulse" />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="profile-header">
                    <h1 className="profile-title">Personal details</h1>
                    <p className="profile-subtitle">
                      Your name, email and phone
                    </p>
                  </div>
                  <div className="profile-section">
                    <div className="profile-info-grid">
                      <div className="profile-info-item">
                        <label
                          className="profile-info-label"
                          htmlFor="profile-edit-name"
                        >
                          Full Name
                        </label>
                        <input
                          id="profile-edit-name"
                          type="text"
                          className="profile-info-input"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          placeholder="Your name"
                        />
                      </div>
                      <div className="profile-info-item">
                        <label className="profile-info-label">
                          Email Address
                        </label>
                        <p className="profile-info-value">
                          {userEmail || "Not provided"}
                        </p>
                      </div>
                      <div className="profile-info-item">
                        <label className="profile-info-label">
                          Phone Number
                        </label>
                        <p className="profile-info-value">
                          {userPhone || "Not provided"}
                        </p>
                      </div>
                      <div className="profile-info-item">
                        <label
                          className="profile-info-label"
                          htmlFor="profile-edit-city"
                        >
                          Your City
                        </label>
                        <select
                          id="profile-edit-city"
                          className="profile-info-select"
                          value={editCity}
                          onChange={(e) => setEditCity(e.target.value)}
                        >
                          <option value="">Select city</option>
                          {cities.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="profile-update-actions">
                      <button
                        type="button"
                        className="profile-update-details-btn"
                        disabled={!hasDetailsChanged}
                        onClick={handleUpdateDetails}
                      >
                        {userData?.postUsersLoading
                          ? "Updating..."
                          : "Update details"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          {activeSection === ENQUIRY_LIST_KEY && (
            <div className="profile-content-single profile-enquiries">
              <div className="profile-header">
                <h1 className="profile-title">Your Enquiries</h1>
                <p className="profile-subtitle">
                  View and manage your event enquiries
                </p>
              </div>
              {enquiriesLoading ? (
                <ul
                  className="profile-enquiries-list profile-enquiries-skeleton"
                  aria-hidden="true"
                >
                  {[1, 2, 3].map((i) => (
                    <li
                      key={i}
                      className="profile-enquiry-item profile-enquiry-skeleton-item"
                    >
                      <div className="profile-enquiry-skeleton-summary skeleton-pulse" />
                      <div className="profile-enquiry-skeleton-btn skeleton-pulse" />
                    </li>
                  ))}
                </ul>
              ) : enquiriesError ? (
                <div className="profile-enquiries-error">
                  {enquiryState?.getMessage || "Failed to load enquiries."}
                </div>
              ) : enquiriesList.length === 0 ? (
                <div className="profile-enquiries-empty">
                  <p>You haven’t submitted any enquiries yet.</p>
                  <button
                    type="button"
                    className="profile-add-enquiry-btn"
                    onClick={() => navigate("/enquiry")}
                  >
                    Add enquiry
                  </button>
                </div>
              ) : (
                <>
                  <ul className="profile-enquiries-list">
                    {enquiriesList
                      .slice(0, enquiriesVisibleCount)
                      .map((item, index) => (
                        <li
                          key={item.id ?? index}
                          className="profile-enquiry-item"
                        >
                          <div className="profile-enquiry-summary">
                            {formatEnquirySummary(item)}
                          </div>
                          <button
                            type="button"
                            className="profile-enquiry-view-details"
                            onClick={() => openEnquiryDetail(item)}
                          >
                            View details
                          </button>
                        </li>
                      ))}
                  </ul>
                  {enquiriesList.length > enquiriesVisibleCount && (
                    <div className="profile-enquiries-load-more-wrap">
                      <button
                        type="button"
                        className="profile-enquiries-load-more"
                        onClick={() =>
                          setEnquiriesVisibleCount(
                            (prev) => prev + ENQUIRIES_PAGE_SIZE,
                          )
                        }
                      >
                        Load more
                      </button>
                    </div>
                  )}
                </>
              )}
              <button
                type="button"
                className="profile-add-enquiry-btn"
                onClick={() => navigate("/enquiry")}
              >
                Add Another Enquiry
              </button>
            </div>
          )}
          {isBusiness && [1, 2, 3, 4].includes(activeSection) && (
            <div className="profile-content-single profile-content-business">
              <BusinessCompletionProfile
                embedInProfile
                activeStep={Number(activeSection)}
              />
            </div>
          )}
          {isBusiness && activeSection === "ratings" && (
          <>
            {profileUserData?.reviews?.length > 0 ?<div className="profile-content-single profile-content-ratings">
              <h3 className="profile-section-title">Business Ratings</h3>
              <div className="profile-ratings-summary">
                <div className="profile-ratings-overall">
                  <span className="profile-ratings-score">
                    {profileUserData?.average_rating}
                  </span>
                  {profileUserData?.average_rating >= 5 ? (
                    <span className="profile-ratings-stars">★★★★★</span>
                  ) : profileUserData?.average_rating >= 4 ? (
                    <span className="profile-ratings-stars">★★★★☆</span>
                  ) : profileUserData?.average_rating >= 3 ? (
                    <span className="profile-ratings-stars">★★★☆☆</span>
                  ) : profileUserData?.average_rating >= 2 ? (
                    <span className="profile-ratings-stars">★★☆☆☆</span>
                  ) : (
                    <span className="profile-ratings-stars">★☆☆☆☆</span>
                  )}

                  <span className="profile-ratings-count">
                    Based on {profileUserData?.total_reviews} reviews
                  </span>
                </div>
              </div>
              <div className="profile-ratings-recent">
                <h4 className="profile-ratings-recent-title">Recent reviews</h4>
                {profileUserData?.reviews?.length > 0 && (
                  <ul className="profile-ratings-list">
                    {profileUserData?.reviews?.map((review) => (
                      <li className="profile-ratings-item" key={review.id}>
                        {review.rating >= 5 ? (
                          <span className="profile-ratings-stars">★★★★★</span>
                        ) : review.rating >= 4 ? (
                          <span className="profile-ratings-stars">★★★★☆</span>
                        ) : review.rating >= 3 ? (
                          <span className="profile-ratings-stars">★★★☆☆</span>
                        ) : review.rating >= 2 ? (
                          <span className="profile-ratings-stars">★★☆☆☆</span>
                        ) : (
                          <span className="profile-ratings-stars">★☆☆☆☆</span>
                        )}
                        <span className="profile-ratings-item-text">
                          {review.review}
                        </span>
                        <span className="profile-ratings-item-meta">
                          —Poster by {review.user_name},{" "}
                          {formatDate(review.created_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>: <div className="profile-content-single profile-content-ratings">
              <h3 className="profile-section-title">Business Ratings</h3>
              <p className="profile-ratings-empty">No ratings yet</p>
            </div>}</>
          )}
        </div>
      </div>

      {/* Enquiry detail modal */}
      {enquiryDetailModal.open && enquiryDetailModal.enquiry && (
        <div
          className="enquiry-detail-modal-overlay"
          onClick={closeEnquiryDetail}
          role="dialog"
          aria-modal="true"
          aria-labelledby="enquiry-detail-title"
        >
          <div
            className="enquiry-detail-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="enquiry-detail-modal-header">
              <h2 id="enquiry-detail-title">Enquiry details</h2>
              <button
                type="button"
                className="enquiry-detail-modal-close"
                onClick={closeEnquiryDetail}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="enquiry-detail-modal-body">
              {Object.entries(enquiryDetailModal.enquiry)
                .filter(
                  ([key, value]) =>
                    value != null &&
                    value !== "" &&
                    ![
                      "id",
                      "created_at",
                      "updated_at",
                      "viewed",
                      "status",
                      "reference_id",
                      "user_name",
                      "userid",
                    ].includes(key),
                )
                .map(([key, value]) => {
                  const label = ENQUIRY_DETAIL_LABELS[key] || key;

                  if (key === "eventdate") {
                    value = formatDate(value);
                  } else if (key === "eventtype" || key === "type") {
                    value = capsFirstLetter(value);
                  }
                  return (
                    <div key={key} className="enquiry-detail-row">
                      <span className="enquiry-detail-label">{label}</span>
                      <span className="enquiry-detail-value">
                        {typeof value === "object" && value !== null
                          ? JSON.stringify(value)
                          : String(value)}
                      </span>
                    </div>
                  );
                })}
            </div>
            <div className="enquiry-detail-modal-actions">
              <button
                type="button"
                className="enquiry-detail-add-another"
                onClick={handleAddAnotherEnquiry}
              >
                Add another enquiry
              </button>
              <button
                type="button"
                className="enquiry-detail-close-btn"
                onClick={closeEnquiryDetail}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div
          className={`logout-modal-overlay ${isClosing ? "modal-closing" : ""}`}
          onClick={
            !isLoggingOut && !isLogoutSuccess ? handleCancelLogout : undefined
          }
        >
          <div
            className={`logout-modal-content ${isLogoutSuccess ? "modal-success" : ""} ${isClosing ? "modal-closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
          >
            {isLogoutSuccess ? (
              <div className="logout-success-content">
                <div className="logout-success-animation">
                  <div className="logout-success-icon">
                    <svg
                      width="80"
                      height="80"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                  </div>
                </div>
                <h3 className="logout-modal-title">Logged Out Successfully!</h3>
                <p className="logout-modal-message">
                  Redirecting you to homepage...
                </p>
              </div>
            ) : (
              <>
                <div className="logout-modal-header">
                  {isLoggingOut ? (
                    <>
                      <div className="logout-modal-icon logout-loading">
                        <svg
                          className="logout-spinner"
                          width="60"
                          height="60"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            strokeDasharray="60"
                            strokeDashoffset="30"
                          >
                            <animate
                              attributeName="stroke-dasharray"
                              values="60;120;60"
                              dur="1.5s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="stroke-dashoffset"
                              values="0;60;0"
                              dur="1.5s"
                              repeatCount="indefinite"
                            />
                          </circle>
                        </svg>
                      </div>
                      <h3 className="logout-modal-title">Logging out...</h3>
                      <p className="logout-modal-message">
                        Please wait while we log you out.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="logout-modal-icon">⚠️</div>
                      <h3 className="logout-modal-title">Are you sure?</h3>
                      <p className="logout-modal-message">
                        Do you really want to logout? You'll need to login again
                        to access your profile.
                      </p>
                    </>
                  )}
                </div>
                {!isLoggingOut && (
                  <div className="logout-modal-actions">
                    <button
                      className="logout-modal-btn logout-modal-cancel"
                      onClick={handleCancelLogout}
                    >
                      Cancel
                    </button>
                    <button
                      className="logout-modal-btn logout-modal-confirm"
                      onClick={handleConfirmLogout}
                    >
                      Yes, Logout
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
